import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, Info, Send, AlertCircle, ShieldAlert, ChevronDown, ChevronUp } from "lucide-react";

interface NotificationSettingsProps {
  telegramEnabled: boolean | null;
  setTelegramEnabled: (val: boolean | null) => void;
  telegramNotifyTypes: string[];
  setTelegramNotifyTypes: (val: string[]) => void;
  isLive: boolean;
  isReadOnly?: boolean;
}

const NOTIFY_OPTIONS = [
  { value: 'entry', label: '신규 진입', desc: '첫 포지션 진입 시그널' },
  { value: 'scaling', label: '추가 물타기', desc: 'DCA 추가 매수 시그널' },
  { value: 'tp', label: '목표 익절', desc: '목표 익절(Take Profit) 완료' },
  { value: 'sl', label: '손절 처리', desc: '손절(Stop Loss) 처리' },
  { value: 'liquidate', label: '청산/상계', desc: '수동 청산 및 수익 상계' },
  { value: 'error', label: '에러 발생', desc: '봇 가동 중 시스템 에러 발생' },
  { value: 'info', label: '일반 알림', desc: '포트폴리오 현황, 서킷 브레이커 등 정보' }
];

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  telegramEnabled,
  setTelegramEnabled,
  telegramNotifyTypes,
  setTelegramNotifyTypes,
  isLive,
  isReadOnly = false
}) => {
  const currentNotifyTypes = telegramNotifyTypes || [];
  const [showDetails, setShowDetails] = useState(false);

  // 실제로 텔레그램이 활성화되는지 여부 판정 (nil일 때 기본값 규칙 반영)
  const isActuallyEnabled = telegramEnabled === null ? isLive : telegramEnabled;

  const handleModeChange = (mode: 'default' | 'on' | 'off') => {
    if (isReadOnly) return;
    if (mode === 'default') {
      setTelegramEnabled(null);
    } else if (mode === 'on') {
      setTelegramEnabled(true);
    } else {
      setTelegramEnabled(false);
    }
  };

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (isReadOnly) return;
    if (checked) {
      setTelegramNotifyTypes([...currentNotifyTypes, value]);
    } else {
      setTelegramNotifyTypes(currentNotifyTypes.filter(t => t !== value));
    }
  };

  const handleSelectAll = () => {
    if (isReadOnly) return;
    setTelegramNotifyTypes(NOTIFY_OPTIONS.map(opt => opt.value));
  };

  const handleDeselectAll = () => {
    if (isReadOnly) return;
    setTelegramNotifyTypes([]);
  };

  return (
    <div className="flex flex-col gap-6 p-6 rounded-2xl bg-muted/20 border border-border/80">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          <h3 className="text-sm font-bold text-foreground">텔레그램 알림 설정 (Telegram Notifications)</h3>
        </div>
        <Badge variant="outline" className="text-[9px] font-bold border-primary/30 text-primary">
          Notifications
        </Badge>
      </div>

      {/* 알림 사용 여부 세그먼트 컨트롤 */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl bg-background/50 border border-border/50">
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-bold text-foreground">알림 전송 모드</Label>
          <span className="text-[10px] text-muted-foreground">
            봇별로 텔레그램 메시지 발송 여부를 세부 제어합니다.
          </span>
        </div>
        <div className="flex bg-muted/60 p-0.5 rounded-lg border border-border/30 w-full md:w-auto">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isReadOnly}
            onClick={() => handleModeChange('default')}
            className={`flex-1 md:flex-initial text-[10px] font-bold px-3 py-1.5 h-auto rounded-md transition-all ${
              telegramEnabled === null
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            기본값 사용
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isReadOnly}
            onClick={() => handleModeChange('on')}
            className={`flex-1 md:flex-initial text-[10px] font-bold px-3 py-1.5 h-auto rounded-md transition-all ${
              telegramEnabled === true
                ? 'bg-success text-white shadow-sm hover:bg-success/90'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            켜기 (ON)
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isReadOnly}
            onClick={() => handleModeChange('off')}
            className={`flex-1 md:flex-initial text-[10px] font-bold px-3 py-1.5 h-auto rounded-md transition-all ${
              telegramEnabled === false
                ? 'bg-danger text-white shadow-sm hover:bg-danger/90'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            끄기 (OFF)
          </Button>
        </div>
      </div>

      {/* 상태 가이드 및 설명 텍스트 */}
      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-primary/5 border border-primary/10 text-[11px] font-medium leading-relaxed text-muted-foreground">
        <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5">
          {telegramEnabled === null ? (
            <>
              <p className="font-bold text-foreground">현재 상태: 기본값 정책 적용 중</p>
              <p>
                - {isLive ? (
                  <span className="text-success font-bold">실매매 (Live) 봇</span>
                ) : (
                  <span className="text-amber-500 font-bold">시뮬레이션 (Paper) 봇</span>
                )}
                이므로 텔레그램 알림이 기본적으로{' '}
                {isLive ? (
                  <strong className="text-success">활성화 (ON)</strong>
                ) : (
                  <strong className="text-amber-500">비활성화 (OFF)</strong>
                )}
                {' '}상태로 동작합니다.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-foreground">
                현재 상태: 사용자 설정 적용 중 ({telegramEnabled ? (
                  <span className="text-success">활성화</span>
                ) : (
                  <span className="text-danger">비활성화</span>
                )})
              </p>
              <p>
                - 트레이딩 모드와 관계없이 항상 알림이{' '}
                {telegramEnabled ? (
                  <strong className="text-success">발송됩니다</strong>
                ) : (
                  <strong className="text-danger">차단됩니다</strong>
                )}
                .
              </p>
            </>
          )}
        </div>
      </div>

      {/* 세부 알림 조건 설정 (활성화 상태일 때만 체크박스 활성화) */}
      {isActuallyEnabled && (
        <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
          <div 
            className="flex items-center justify-between border-b border-border/50 pb-2 cursor-pointer hover:opacity-80 transition-all select-none"
            onClick={() => setShowDetails(!showDetails)}
          >
            <div className="flex items-center gap-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wider cursor-pointer">세부 알림 조건</Label>
              {showDetails ? (
                <ChevronUp className="w-3.5 h-3.5 text-muted-foreground transition-transform" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground transition-transform" />
              )}
            </div>
            {showDetails && (
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={handleSelectAll}
                  className="h-6 text-[9px] font-bold text-primary hover:bg-primary/5 px-2 rounded"
                >
                  전체 선택
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={handleDeselectAll}
                  className="h-6 text-[9px] font-bold text-muted-foreground hover:bg-muted px-2 rounded"
                >
                  전체 해제
                </Button>
              </div>
            )}
          </div>

          {showDetails && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 animate-in fade-in slide-in-from-top-1 duration-200">
              {NOTIFY_OPTIONS.map((opt) => {
                const isChecked = currentNotifyTypes.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                      isChecked
                        ? 'bg-primary/5 border-primary/20'
                        : 'bg-background/20 border-border/40 opacity-70'
                    }`}
                  >
                    <Checkbox
                      id={`notify-${opt.value}`}
                      checked={isChecked}
                      disabled={isReadOnly}
                      onCheckedChange={(checked) => handleCheckboxChange(opt.value, !!checked)}
                      className="border-primary/30 data-[state=checked]:bg-primary h-4 w-4 mt-0.5"
                    />
                    <div className="flex flex-col gap-0.5 cursor-pointer" onClick={() => !isReadOnly && handleCheckboxChange(opt.value, !isChecked)}>
                      <Label
                        htmlFor={`notify-${opt.value}`}
                        className="text-xs font-bold text-foreground cursor-pointer"
                      >
                        {opt.label}
                      </Label>
                      <span className="text-[10px] text-muted-foreground font-medium leading-tight">
                        {opt.desc}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
